import type { GradingRole, GradingTreePayload } from '../grading/types';
import { normalizeExcelName } from './recordExcelCompanyResolve';
import type { RecordExcelParsedRow } from './recordExcelParser';

export type RoleLinkStatus =
  | 'matched_by_name'
  | 'matched_by_family_grade'
  | 'fallback'
  | 'family_missing'
  | 'family_not_found'
  | 'grade_invalid'
  | 'grade_not_found_in_family'
  | 'role_not_found';

export type RoleRowResolution = {
  rowNumber: number;
  status: RoleLinkStatus;
  message: string;
  roleId?: number;
  roleName?: string;
  gradeLevel?: number;
  familyId?: number;
  familyName?: string;
};

function parseGradeLevel(raw: string | null | undefined): number | null {
  const gradeRaw = String(raw || '').trim();
  if (!gradeRaw) return null;
  const gradeLevel = Number(gradeRaw);
  return Number.isFinite(gradeLevel) ? gradeLevel : null;
}

function findFamilyByName(tree: GradingTreePayload, familyName: string) {
  const norm = normalizeExcelName(familyName);
  return tree.families.find((f) => normalizeExcelName(f.name) === norm);
}

function findRoleByName(tree: GradingTreePayload, roleName: string): GradingRole | undefined {
  const norm = normalizeExcelName(roleName);
  return tree.roles.find((r) => normalizeExcelName(r.name) === norm);
}

function rolesInFamily(tree: GradingTreePayload, familyId: number): GradingRole[] {
  const clusterIds = new Set(
    tree.roleClusters.filter((rc) => rc.family_id === familyId).map((rc) => rc.id),
  );
  return tree.roles.filter((r) => clusterIds.has(r.role_cluster_id));
}

/** При нескольких ролях на одном грейде — младшая (min_score ↑, затем id). */
export function pickJuniorRole(roles: GradingRole[]): GradingRole | undefined {
  if (roles.length === 0) return undefined;
  return [...roles].sort((a, b) => {
    const minA = a.min_score ?? Number.POSITIVE_INFINITY;
    const minB = b.min_score ?? Number.POSITIVE_INFINITY;
    if (minA !== minB) return minA - minB;
    return a.id - b.id;
  })[0];
}

export function isRoleResolutionError(status: RoleLinkStatus): boolean {
  return status !== 'matched_by_name'
    && status !== 'matched_by_family_grade'
    && status !== 'fallback';
}

export function resolveRoleForRow(
  row: RecordExcelParsedRow,
  tree: GradingTreePayload,
): RoleRowResolution {
  const { rowNumber, values } = row;
  const roleLabel = values.roleName?.trim() || '';
  const familyLabel = values.familyName?.trim() || '';
  const gradeLevel = parseGradeLevel(values.datacircleGrade);

  if (roleLabel) {
    const role = findRoleByName(tree, roleLabel);
    if (!role) {
      return {
        rowNumber,
        status: 'role_not_found',
        message: `Роль «${roleLabel}» не найдена в справочнике (Z)`,
      };
    }
    return {
      rowNumber,
      status: 'matched_by_name',
      roleId: role.id,
      roleName: role.name,
      gradeLevel: role.grade_level,
      message: `Роль: ${role.name} · G${role.grade_level}`,
    };
  }

  if (gradeLevel != null) {
    if (!familyLabel) {
      return {
        rowNumber,
        status: 'family_missing',
        message: 'Указан грейд (V), но не указана семья (W)',
      };
    }

    const family = findFamilyByName(tree, familyLabel);
    if (!family) {
      return {
        rowNumber,
        status: 'family_not_found',
        familyName: familyLabel,
        message: `Семья «${familyLabel}» не найдена (W)`,
      };
    }

    const candidates = rolesInFamily(tree, family.id).filter((r) => r.grade_level === gradeLevel);
    const role = pickJuniorRole(candidates);
    if (!role) {
      return {
        rowNumber,
        status: 'grade_not_found_in_family',
        familyId: family.id,
        familyName: family.name,
        gradeLevel,
        message: `В семье «${family.name}» нет роли с грейдом G${gradeLevel}`,
      };
    }

    const suffix = candidates.length > 1 ? ` (младшая из ${candidates.length})` : '';
    return {
      rowNumber,
      status: 'matched_by_family_grade',
      roleId: role.id,
      roleName: role.name,
      gradeLevel: role.grade_level,
      familyId: family.id,
      familyName: family.name,
      message: `${family.name} · G${gradeLevel} → ${role.name}${suffix}`,
    };
  }

  if (values.datacircleGrade?.trim() && gradeLevel == null) {
    return {
      rowNumber,
      status: 'grade_invalid',
      message: `Некорректный грейд «${values.datacircleGrade}» (V)`,
    };
  }

  return {
    rowNumber,
    status: 'fallback',
    message: 'Роль будет подобрана по должности (H) или fallback при сохранении',
  };
}

export function resolveRoleRows(
  rows: RecordExcelParsedRow[],
  tree: GradingTreePayload,
): RoleRowResolution[] {
  return rows.map((row) => resolveRoleForRow(row, tree));
}

export function roleResolutionForRow(
  resolutions: RoleRowResolution[],
  rowNumber: number,
): RoleRowResolution | undefined {
  return resolutions.find((r) => r.rowNumber === rowNumber);
}
